/**
 * API Response Utilities for ArbiWin
 *
 * This module provides comprehensive response processing utilities for all API
 * interactions, including data validation, transformation, pagination handling,
 * caching mechanisms, performance monitoring, and development mocking.
 *
 * Features:
 * - Response data validation and schema checking
 * - Data transformation and normalization
 * - Pagination handling and metadata extraction
 * - Response caching with TTL and invalidation
 * - Performance monitoring and metrics
 * - Development mocking and testing utilities
 * - MarketsAnalysisDTO specific processing
 *
 * Following constitution requirements for surebet data handling
 * and performance optimization.
 */

import { dateFormatters } from '../../../utils/formatters';
import { basicValidators } from '../../../utils/validators';

/**
 * Response Status Constants
 */
export const RESPONSE_STATUS = {
  SUCCESS: 'success',
  ERROR: 'error',
  PARTIAL: 'partial',
  EMPTY: 'empty',
  CACHED: 'cached'
};

/**
 * Cache Configuration
 */
const CACHE_CONFIG = {
  DEFAULT_TTL: 300000, // 5 minutes
  MAX_CACHE_SIZE: 100,
  CLEANUP_INTERVAL: 600000, // 10 minutes
  STORAGE_PREFIX: 'arbiwin_api_cache_'
};

/**
 * Response Schema Definitions
 * Following constitution requirements for MarketsAnalysisDTO structure
 */
const RESPONSE_SCHEMAS = {
  // MarketsAnalysisDTO schema (critical for surebet data)
  MARKETS_ANALYSIS: {
    type: 'array',
    items: {
      type: 'object',
      required: ['id', 'event', 'marginFlag', 'margins'],
      properties: {
        id: { type: 'number' },
        event: {
          type: 'object',
          required: ['id', 'date', 'time', 'teamOne', 'teamTwo', 'championship', 'sport'],
          properties: {
            id: { type: 'number' },
            date: { type: 'string', pattern: '^\\d{4}-\\d{2}-\\d{2}$' },
            time: { type: 'string', pattern: '^\\d{2}:\\d{2}$' },
            teamOne: { type: 'string', minLength: 1 },
            teamTwo: { type: 'string', minLength: 1 },
            championship: { type: 'string', minLength: 1 },
            sport: { type: 'string', minLength: 1 }
          }
        },
        marginFlag: { type: 'boolean' },
        margins: {
          type: 'object',
          // Dynamic object - margin types are conditional based on data availability
          patternProperties: {
            '^margin.*': {
              type: 'object',
              required: ['value', 'coefficients'],
              properties: {
                value: { type: 'number' },
                coefficients: {
                  type: 'array',
                  items: {
                    type: 'object',
                    required: ['bookmaker', 'type', 'value'],
                    properties: {
                      bookmaker: { type: 'string', minLength: 1 },
                      type: { type: 'string', minLength: 1 },
                      value: { type: 'number', minimum: 1.0 }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  },

  // Authentication response schema
  AUTH_RESPONSE: {
    type: 'object',
    required: ['accessToken', 'refreshToken', 'expiresIn', 'user'],
    properties: {
      accessToken: { type: 'string', minLength: 10 },
      refreshToken: { type: 'string', minLength: 10 },
      expiresIn: { type: 'number', minimum: 0 },
      user: {
        type: 'object',
        required: ['id', 'email', 'role'],
        properties: {
          id: { type: 'number' },
          email: { type: 'string', format: 'email' },
          role: { type: 'string', enum: ['User', 'Admin', 'Super Admin'] },
          firstName: { type: 'string' },
          lastName: { type: 'string' }
        }
      }
    }
  },

  // Subscription status schema
  SUBSCRIPTION_STATUS: {
    type: 'object',
    required: ['isActive'],
    properties: {
      isActive: { type: 'boolean' },
      expiryDate: { type: 'string', format: 'date-time' },
      renewalDate: { type: 'string', format: 'date-time' },
      plan: { type: 'string' }
    }
  },

  // Pagination wrapper schema
  PAGINATED_RESPONSE: {
    type: 'object',
    required: ['data', 'pagination'],
    properties: {
      data: { type: 'array' },
      pagination: {
        type: 'object',
        required: ['page', 'size', 'total', 'totalPages'],
        properties: {
          page: { type: 'number', minimum: 0 },
          size: { type: 'number', minimum: 1 },
          total: { type: 'number', minimum: 0 },
          totalPages: { type: 'number', minimum: 0 },
          hasNext: { type: 'boolean' },
          hasPrevious: { type: 'boolean' }
        }
      }
    }
  },

  // Reference data schemas
  BOOKMAKERS: {
    type: 'array',
    items: {
      type: 'object',
      required: ['id', 'name', 'code'],
      properties: {
        id: { type: 'number' },
        name: { type: 'string', minLength: 1 },
        code: { type: 'string', minLength: 1 },
        active: { type: 'boolean' },
        country: { type: 'string' },
        website: { type: 'string', format: 'uri' }
      }
    }
  },

  SPORTS: {
    type: 'array',
    items: {
      type: 'object',
      required: ['id', 'name', 'code'],
      properties: {
        id: { type: 'number' },
        name: { type: 'string', minLength: 1 },
        code: { type: 'string', minLength: 1 },
        active: { type: 'boolean' }
      }
    }
  }
};

/**
 * Response Cache Manager
 */
class ResponseCache {
  constructor() {
    this.cache = new Map();
    this.timestamps = new Map();
    this.accessCounts = new Map();

    // Start cleanup interval
    this.startCleanupInterval();
  }

  /**
   * Generate cache key from request parameters
   * @param {string} url - Request URL
   * @param {Object} params - Request parameters
   * @returns {string} Cache key
   */
  generateKey(url, params = {}) {
    const paramString = Object.keys(params)
      .sort()
      .map(key => `${key}=${JSON.stringify(params[key])}`)
      .join('&');

    return `${url}?${paramString}`;
  }

  /**
   * Set cached response
   * @param {string} key - Cache key
   * @param {*} data - Response data
   * @param {number} ttl - Time to live in milliseconds
   */
  set(key, data, ttl = CACHE_CONFIG.DEFAULT_TTL) {
    // Check cache size limit
    if (this.cache.size >= CACHE_CONFIG.MAX_CACHE_SIZE) {
      this.evictLeastUsed();
    }

    this.cache.set(key, data);
    this.timestamps.set(key, Date.now() + ttl);
    this.accessCounts.set(key, 0);
  }

  /**
   * Get cached response
   * @param {string} key - Cache key
   * @returns {*} Cached data or null
   */
  get(key) {
    if (!this.cache.has(key)) {
      return null;
    }

    const expiry = this.timestamps.get(key);
    if (Date.now() > expiry) {
      this.delete(key);
      return null;
    }

    // Update access count
    const count = this.accessCounts.get(key) || 0;
    this.accessCounts.set(key, count + 1);

    return this.cache.get(key);
  }

  /**
   * Delete cached response
   * @param {string} key - Cache key
   */
  delete(key) {
    this.cache.delete(key);
    this.timestamps.delete(key);
    this.accessCounts.delete(key);
  }

  /**
   * Clear all cached responses
   */
  clear() {
    this.cache.clear();
    this.timestamps.clear();
    this.accessCounts.clear();
  }

  /**
   * Evict least used cache entry
   */
  evictLeastUsed() {
    let leastUsedKey = null;
    let leastUsedCount = Infinity;

    for (const [key, count] of this.accessCounts.entries()) {
      if (count < leastUsedCount) {
        leastUsedCount = count;
        leastUsedKey = key;
      }
    }

    if (leastUsedKey) {
      this.delete(leastUsedKey);
    }
  }

  /**
   * Start automatic cleanup interval
   */
  startCleanupInterval() {
    setInterval(() => {
      const now = Date.now();
      const expiredKeys = [];

      for (const [key, expiry] of this.timestamps.entries()) {
        if (now > expiry) {
          expiredKeys.push(key);
        }
      }

      expiredKeys.forEach(key => this.delete(key));

    }, CACHE_CONFIG.CLEANUP_INTERVAL);
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache statistics
   */
  getStats() {
    return {
      size: this.cache.size,
      maxSize: CACHE_CONFIG.MAX_CACHE_SIZE,
      keys: Array.from(this.cache.keys()),
      accessCounts: Object.fromEntries(this.accessCounts)
    };
  }
}

/**
 * Response Performance Monitor
 */
class ResponsePerformanceMonitor {
  constructor() {
    this.metrics = {
      totalRequests: 0,
      totalResponseTime: 0,
      averageResponseTime: 0,
      slowRequests: 0,
      fastRequests: 0,
      cacheHits: 0,
      cacheMisses: 0,
      validationErrors: 0,
      transformationErrors: 0
    };

    this.requestTimes = new Map();
    this.slowRequestThreshold = 2000; // 2 seconds
    this.fastRequestThreshold = 500; // 0.5 seconds
  }

  /**
   * Start timing a request
   * @param {string} requestId - Unique request identifier
   */
  startTiming(requestId) {
    this.requestTimes.set(requestId, performance.now());
  }

  /**
   * End timing a request and record metrics
   * @param {string} requestId - Request identifier
   * @param {Object} metadata - Additional metadata
   */
  endTiming(requestId, metadata = {}) {
    const startTime = this.requestTimes.get(requestId);
    if (!startTime) return;

    const duration = performance.now() - startTime;
    this.requestTimes.delete(requestId);

    // Update metrics
    this.metrics.totalRequests++;
    this.metrics.totalResponseTime += duration;
    this.metrics.averageResponseTime = this.metrics.totalResponseTime / this.metrics.totalRequests;

    if (duration > this.slowRequestThreshold) {
      this.metrics.slowRequests++;
    } else if (duration < this.fastRequestThreshold) {
      this.metrics.fastRequests++;
    }

    // Log slow requests
    if (duration > this.slowRequestThreshold) {
      console.warn(`🐌 Slow API response: ${duration.toFixed(2)}ms`, metadata);
    }
  }

  /**
   * Record cache hit
   */
  recordCacheHit() {
    this.metrics.cacheHits++;
  }

  /**
   * Record cache miss
   */
  recordCacheMiss() {
    this.metrics.cacheMisses++;
  }

  /**
   * Record validation error
   */
  recordValidationError() {
    this.metrics.validationErrors++;
  }

  /**
   * Record transformation error
   */
  recordTransformationError() {
    this.metrics.transformationErrors++;
  }

  /**
   * Get performance metrics
   * @returns {Object} Performance metrics
   */
  getMetrics() {
    return {
      ...this.metrics,
      cacheHitRate: this.metrics.cacheHits / (this.metrics.cacheHits + this.metrics.cacheMisses) || 0,
      slowRequestRate: this.metrics.slowRequests / this.metrics.totalRequests || 0,
      fastRequestRate: this.metrics.fastRequests / this.metrics.totalRequests || 0
    };
  }

  /**
   * Reset all metrics
   */
  reset() {
    this.metrics = {
      totalRequests: 0,
      totalResponseTime: 0,
      averageResponseTime: 0,
      slowRequests: 0,
      fastRequests: 0,
      cacheHits: 0,
      cacheMisses: 0,
      validationErrors: 0,
      transformationErrors: 0
    };
    this.requestTimes.clear();
  }
}

/**
 * Response Validator
 */
class ResponseValidator {
  /**
   * Validate response against schema
   * @param {*} data - Response data
   * @param {Object} schema - Validation schema
   * @returns {Object} Validation result
   */
  static validate(data, schema) {
    const result = {
      isValid: true,
      errors: [],
      warnings: []
    };

    try {
      const validation = this.validateRecursive(data, schema, '');
      return validation;
    } catch (error) {
      result.isValid = false;
      result.errors.push(`Validation error: ${error.message}`);
      return result;
    }
  }

  /**
   * Recursive validation helper
   * @param {*} data - Data to validate
   * @param {Object} schema - Schema to validate against
   * @param {string} path - Current path in data structure
   * @returns {Object} Validation result
   */
  static validateRecursive(data, schema, path) {
    const result = {
      isValid: true,
      errors: [],
      warnings: []
    };

    // Type validation
    if (schema.type) {
      const dataType = Array.isArray(data) ? 'array' : typeof data;
      if (dataType !== schema.type) {
        result.isValid = false;
        result.errors.push(`${path}: Expected ${schema.type}, got ${dataType}`);
        return result;
      }
    }

    // Required properties validation
    if (schema.required && schema.type === 'object') {
      for (const requiredProp of schema.required) {
        if (!(requiredProp in data)) {
          result.isValid = false;
          result.errors.push(`${path}: Missing required property '${requiredProp}'`);
        }
      }
    }

    // Properties validation
    if (schema.properties && schema.type === 'object') {
      for (const [propName, propSchema] of Object.entries(schema.properties)) {
        if (propName in data) {
          const propPath = path ? `${path}.${propName}` : propName;
          const propResult = this.validateRecursive(data[propName], propSchema, propPath);

          result.errors.push(...propResult.errors);
          result.warnings.push(...propResult.warnings);

          if (!propResult.isValid) {
            result.isValid = false;
          }
        }
      }
    }

    // Pattern properties validation (for dynamic object keys)
    if (schema.patternProperties && schema.type === 'object') {
      for (const [pattern, propSchema] of Object.entries(schema.patternProperties)) {
        const regex = new RegExp(pattern);

        for (const [key, value] of Object.entries(data)) {
          if (regex.test(key)) {
            const propPath = path ? `${path}.${key}` : key;
            const propResult = this.validateRecursive(value, propSchema, propPath);

            result.errors.push(...propResult.errors);
            result.warnings.push(...propResult.warnings);

            if (!propResult.isValid) {
              result.isValid = false;
            }
          }
        }
      }
    }

    // Array items validation
    if (schema.items && schema.type === 'array') {
      data.forEach((item, index) => {
        const itemPath = `${path}[${index}]`;
        const itemResult = this.validateRecursive(item, schema.items, itemPath);

        result.errors.push(...itemResult.errors);
        result.warnings.push(...itemResult.warnings);

        if (!itemResult.isValid) {
          result.isValid = false;
        }
      });
    }

    // String format validation
    if (schema.format && typeof data === 'string') {
      const formatResult = this.validateFormat(data, schema.format, path);
      result.errors.push(...formatResult.errors);
      result.warnings.push(...formatResult.warnings);

      if (!formatResult.isValid) {
        result.isValid = false;
      }
    }

    // String pattern validation
    if (schema.pattern && typeof data === 'string') {
      const regex = new RegExp(schema.pattern);
      if (!regex.test(data)) {
        result.isValid = false;
        result.errors.push(`${path}: String does not match pattern ${schema.pattern}`);
      }
    }

    // Number validation
    if (typeof data === 'number') {
      if (schema.minimum !== undefined && data < schema.minimum) {
        result.isValid = false;
        result.errors.push(`${path}: Value ${data} is below minimum ${schema.minimum}`);
      }

      if (schema.maximum !== undefined && data > schema.maximum) {
        result.isValid = false;
        result.errors.push(`${path}: Value ${data} is above maximum ${schema.maximum}`);
      }
    }

    // String length validation
    if (typeof data === 'string') {
      if (schema.minLength !== undefined && data.length < schema.minLength) {
        result.isValid = false;
        result.errors.push(`${path}: String length ${data.length} is below minimum ${schema.minLength}`);
      }

      if (schema.maxLength !== undefined && data.length > schema.maxLength) {
        result.isValid = false;
        result.errors.push(`${path}: String length ${data.length} is above maximum ${schema.maxLength}`);
      }
    }

    // Enum validation
    if (schema.enum && !schema.enum.includes(data)) {
      result.isValid = false;
      result.errors.push(`${path}: Value '${data}' is not in allowed enum values: ${schema.enum.join(', ')}`);
    }

    return result;
  }

  /**
   * Validate string format
   * @param {string} value - String value to validate
   * @param {string} format - Format to validate against
   * @param {string} path - Path in data structure
   * @returns {Object} Validation result
   */
  static validateFormat(value, format, path) {
    const result = {
      isValid: true,
      errors: [],
      warnings: []
    };

    switch (format) {
      case 'email':
        if (!basicValidators.isEmail(value)) {
          result.isValid = false;
          result.errors.push(`${path}: Invalid email format`);
        }
        break;

      case 'uri':
        if (!basicValidators.isURL(value)) {
          result.isValid = false;
          result.errors.push(`${path}: Invalid URI format`);
        }
        break;

      case 'date':
        if (!basicValidators.isDate(value)) {
          result.isValid = false;
          result.errors.push(`${path}: Invalid date format`);
        }
        break;

      case 'date-time':
        if (!basicValidators.isDate(value)) {
          result.isValid = false;
          result.errors.push(`${path}: Invalid date-time format`);
        }
        break;

      default:
        result.warnings.push(`${path}: Unknown format '${format}'`);
    }

    return result;
  }

  /**
   * Validate MarketsAnalysisDTO specifically
   * @param {Array} data - Markets analysis data
   * @returns {Object} Validation result with surebet-specific checks
   */
  static validateMarketsAnalysis(data) {
    const baseValidation = this.validate(data, RESPONSE_SCHEMAS.MARKETS_ANALYSIS);

    if (!baseValidation.isValid) {
      return baseValidation;
    }

    // Additional surebet-specific validation
    const surebetValidation = {
      isValid: true,
      errors: [],
      warnings: []
    };

    data.forEach((item, index) => {
      // Validate marginFlag consistency
      const hasPositiveMargins = Object.values(item.margins || {})
        .some(margin => margin.value > 0);

      if (item.marginFlag && !hasPositiveMargins) {
        surebetValidation.warnings.push(
          `Item ${index}: marginFlag is true but no positive margins found`
        );
      }

      if (!item.marginFlag && hasPositiveMargins) {
        surebetValidation.warnings.push(
          `Item ${index}: marginFlag is false but positive margins exist`
        );
      }

      // Validate margin coefficients completeness
      Object.entries(item.margins || {}).forEach(([marginType, marginData]) => {
        const coefficientTypes = marginData.coefficients.map(c => c.type);
        const uniqueTypes = [...new Set(coefficientTypes)];

        if (coefficientTypes.length !== uniqueTypes.length) {
          surebetValidation.warnings.push(
            `Item ${index}, ${marginType}: Duplicate coefficient types found`
          );
        }

        // Validate bookmaker coverage
        const bookmakers = marginData.coefficients.map(c => c.bookmaker);
        const uniqueBookmakers = [...new Set(bookmakers)];

        if (bookmakers.length !== uniqueBookmakers.length) {
          surebetValidation.warnings.push(
            `Item ${index}, ${marginType}: Duplicate bookmaker entries found`
          );
        }
      });

      // Validate event date/time
      const eventDate = new Date(`${item.event.date}T${item.event.time}`);
      if (eventDate < new Date()) {
        surebetValidation.warnings.push(
          `Item ${index}: Event is in the past (${item.event.date} ${item.event.time})`
        );
      }
    });

    return {
      isValid: baseValidation.isValid && surebetValidation.isValid,
      errors: [...baseValidation.errors, ...surebetValidation.errors],
      warnings: [...baseValidation.warnings, ...surebetValidation.warnings]
    };
  }
}

/**
 * Response Transformer
 */
class ResponseTransformer {
  /**
   * Transform MarketsAnalysisDTO for frontend consumption
   * @param {Array} data - Raw markets analysis data
   * @returns {Array} Transformed data
   */
  static transformMarketsAnalysis(data) {
    if (!Array.isArray(data)) {
      return [];
    }

    return data.map(item => {
      const transformed = {
        ...item,
        // Add computed properties
        profitableMargins: this.extractProfitableMargins(item.margins),
        maxMargin: this.calculateMaxMargin(item.margins),
        bookmakerCount: this.countUniqueBookmakers(item.margins),
        marketTypeCount: Object.keys(item.margins || {}).length,

        // Format event data
        event: {
          ...item.event,
          formattedDate: dateFormatters.formatDate(item.event.date, 'bg'),
          formattedTime: item.event.time,
          displayName: `${item.event.teamOne} vs ${item.event.teamTwo}`,
          eventDateTime: new Date(`${item.event.date}T${item.event.time}`)
        }
      };

      // Transform margins for easier frontend consumption
      transformed.transformedMargins = this.transformMargins(item.margins);

      return transformed;
    });
  }

  /**
   * Extract profitable margins only
   * @param {Object} margins - Margins object
   * @returns {Object} Profitable margins only
   */
  static extractProfitableMargins(margins) {
    const profitable = {};

    Object.entries(margins || {}).forEach(([key, margin]) => {
      if (margin.value > 0) {
        profitable[key] = margin;
      }
    });

    return profitable;
  }

  /**
   * Calculate maximum margin value
   * @param {Object} margins - Margins object
   * @returns {number} Maximum margin value
   */
  static calculateMaxMargin(margins) {
    const values = Object.values(margins || {}).map(m => m.value);
    return values.length > 0 ? Math.max(...values) : 0;
  }

  /**
   * Count unique bookmakers across all margins
   * @param {Object} margins - Margins object
   * @returns {number} Number of unique bookmakers
   */
  static countUniqueBookmakers(margins) {
    const bookmakers = new Set();

    Object.values(margins || {}).forEach(margin => {
      margin.coefficients.forEach(coeff => {
        bookmakers.add(coeff.bookmaker);
      });
    });

    return bookmakers.size;
  }

  /**
   * Transform margins for easier frontend consumption
   * @param {Object} margins - Raw margins object
   * @returns {Array} Array of margin objects with metadata
   */
  static transformMargins(margins) {
    return Object.entries(margins || {}).map(([type, data]) => ({
      type,
      value: data.value,
      percentage: `${(data.value * 100).toFixed(2)}%`,
      isProfitable: data.value > 0,
      coefficients: data.coefficients.map(coeff => ({
        ...coeff,
        formattedValue: coeff.value.toFixed(2)
      })),
      bestOdds: this.findBestOdds(data.coefficients),
      outcomeCount: data.coefficients.length
    }));
  }

  /**
   * Find best odds for each outcome type
   * @param {Array} coefficients - Array of coefficients
   * @returns {Object} Best odds by outcome type
   */
  static findBestOdds(coefficients) {
    const bestOdds = {};

    coefficients.forEach(coeff => {
      if (!bestOdds[coeff.type] || coeff.value > bestOdds[coeff.type].value) {
        bestOdds[coeff.type] = coeff;
      }
    });

    return bestOdds;
  }

  /**
   * Transform pagination metadata
   * @param {Object} pagination - Raw pagination data
   * @returns {Object} Enhanced pagination metadata
   */
  static transformPagination(pagination) {
    if (!pagination) {
      return null;
    }

    return {
      ...pagination,
      hasNext: pagination.page < pagination.totalPages - 1,
      hasPrevious: pagination.page > 0,
      isFirstPage: pagination.page === 0,
      isLastPage: pagination.page === pagination.totalPages - 1,
      startIndex: pagination.page * pagination.size,
      endIndex: Math.min((pagination.page + 1) * pagination.size - 1, pagination.total - 1),
      displayText: {
        bg: `Страница ${pagination.page + 1} от ${pagination.totalPages} (общо ${pagination.total} резултата)`,
        en: `Page ${pagination.page + 1} of ${pagination.totalPages} (${pagination.total} total results)`
      }
    };
  }

  /**
   * Transform user data for frontend consumption
   * @param {Object} userData - Raw user data
   * @returns {Object} Transformed user data
   */
  static transformUserData(userData) {
    if (!userData) {
      return null;
    }

    return {
      ...userData,
      fullName: [userData.firstName, userData.lastName].filter(Boolean).join(' ') || userData.email,
      initials: this.generateInitials(userData.firstName, userData.lastName, userData.email),
      isAdmin: ['Admin', 'Super Admin'].includes(userData.role),
      isSuperAdmin: userData.role === 'Super Admin',
      displayRole: {
        bg: this.translateRole(userData.role, 'bg'),
        en: userData.role
      }
    };
  }

  /**
   * Generate user initials
   * @param {string} firstName - First name
   * @param {string} lastName - Last name
   * @param {string} email - Email address
   * @returns {string} User initials
   */
  static generateInitials(firstName, lastName, email) {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    } else if (firstName) {
      return firstName.substring(0, 2).toUpperCase();
    } else if (email) {
      return email.substring(0, 2).toUpperCase();
    }
    return 'U';
  }

  /**
   * Translate user role to Bulgarian
   * @param {string} role - User role
   * @param {string} language - Target language
   * @returns {string} Translated role
   */
  static translateRole(role, language) {
    const translations = {
      'User': { bg: 'Потребител', en: 'User' },
      'Admin': { bg: 'Администратор', en: 'Admin' },
      'Super Admin': { bg: 'Главен Администратор', en: 'Super Admin' }
    };

    return translations[role]?.[language] || role;
  }
}

/**
 * Pagination Handler
 */
class PaginationHandler {
  /**
   * Process paginated response
   * @param {Object} response - API response with pagination
   * @returns {Object} Processed pagination data
   */
  static processPaginatedResponse(response) {
    const { data, pagination } = response;

    return {
      items: data,
      pagination: ResponseTransformer.transformPagination(pagination),
      isEmpty: !data || data.length === 0,
      isFirstPage: pagination.page === 0,
      isLastPage: pagination.page >= pagination.totalPages - 1,
      totalItems: pagination.total,
      currentPageSize: data ? data.length : 0
    };
  }

  /**
   * Generate pagination parameters for API request
   * @param {number} page - Page number (0-based)
   * @param {number} size - Page size
   * @param {Object} filters - Additional filters
   * @returns {Object} Pagination parameters
   */
  static generatePaginationParams(page = 0, size = 20, filters = {}) {
    return {
      page: Math.max(0, page),
      size: Math.min(Math.max(1, size), 100), // Limit between 1 and 100
      ...filters
    };
  }

  /**
   * Calculate pagination navigation
   * @param {Object} pagination - Pagination metadata
   * @param {number} maxButtons - Maximum pagination buttons to show
   * @returns {Object} Navigation data
   */
  static calculatePaginationNavigation(pagination, maxButtons = 5) {
    if (!pagination || pagination.totalPages <= 1) {
      return { pages: [], showEllipsis: false };
    }

    const current = pagination.page;
    const total = pagination.totalPages;
    const half = Math.floor(maxButtons / 2);

    let start = Math.max(0, current - half);
    let end = Math.min(total - 1, current + half);

    // Adjust if we're at the beginning or end
    if (end - start < maxButtons - 1) {
      if (start === 0) {
        end = Math.min(total - 1, start + maxButtons - 1);
      } else if (end === total - 1) {
        start = Math.max(0, end - maxButtons + 1);
      }
    }

    const pages = [];
    for (let i = start; i <= end; i++) {
      pages.push({
        page: i,
        isCurrent: i === current,
        displayNumber: i + 1
      });
    }

    return {
      pages,
      showEllipsis: {
        start: start > 0,
        end: end < total - 1
      },
      hasFirst: start > 0,
      hasLast: end < total - 1
    };
  }
}

/**
 * Mock Response Generator for Development
 */
class MockResponseGenerator {
  /**
   * Generate mock MarketsAnalysisDTO data
   * @param {number} count - Number of items to generate
   * @returns {Array} Mock markets analysis data
   */
  static generateMockMarketsAnalysis(count = 10) {
    const mockData = [];
    const sports = ['Football', 'Basketball', 'Tennis'];
    const championships = ['Premier League', 'La Liga', 'Serie A', 'Bundesliga'];
    const bookmakers = ['betano', 'winbet', 'bet365', 'bwin'];
    const teams = [
      'Arsenal', 'Chelsea', 'Liverpool', 'Manchester City', 'Barcelona', 'Real Madrid',
      'AC Milan', 'Juventus', 'Bayern Munich', 'Borussia Dortmund'
    ];

    for (let i = 0; i < count; i++) {
      const hasProfit = Math.random() > 0.3; // 70% chance of profitable
      const marginCount = Math.floor(Math.random() * 3) + 1; // 1-3 margins
      const margins = {};

      // Generate margins
      const marginTypes = ['margin1X2', 'marginOverUnder2AndHalf', 'marginBothScore'];
      for (let j = 0; j < marginCount; j++) {
        const marginType = marginTypes[j];
        const marginValue = hasProfit ? Math.random() * 0.05 + 0.01 : Math.random() * -0.02;

        margins[marginType] = {
          value: marginValue,
          coefficients: this.generateMockCoefficients(marginType, bookmakers)
        };
      }

      const teamOne = teams[Math.floor(Math.random() * teams.length)];
      let teamTwo = teams[Math.floor(Math.random() * teams.length)];
      while (teamTwo === teamOne) {
        teamTwo = teams[Math.floor(Math.random() * teams.length)];
      }

      const eventDate = new Date();
      eventDate.setDate(eventDate.getDate() + Math.floor(Math.random() * 7) + 1);

      mockData.push({
        id: i + 1,
        event: {
          id: i + 1,
          date: eventDate.toISOString().split('T')[0],
          time: `${String(Math.floor(Math.random() * 12) + 12).padStart(2, '0')}:${String(Math.floor(Math.random() * 60)).padStart(2, '0')}`,
          teamOne,
          teamTwo,
          championship: championships[Math.floor(Math.random() * championships.length)],
          sport: sports[Math.floor(Math.random() * sports.length)]
        },
        marginFlag: hasProfit,
        margins
      });
    }

    return mockData;
  }

  /**
   * Generate mock coefficients for a margin type
   * @param {string} marginType - Type of margin
   * @param {Array} bookmakers - Available bookmakers
   * @returns {Array} Mock coefficients
   */
  static generateMockCoefficients(marginType, bookmakers) {
    const coefficientTypes = {
      'margin1X2': ['Coef1', 'CoefX', 'Coef2'],
      'marginOverUnder2AndHalf': ['CoefGoalOver2AndHalf', 'CoefGoalUnder2AndHalf'],
      'marginBothScore': ['CoefBothScoreYes', 'CoefBothScoreNo']
    };

    const types = coefficientTypes[marginType] || ['Coef1', 'CoefX', 'Coef2'];
    const coefficients = [];

    types.forEach(type => {
      const selectedBookmakers = bookmakers.slice(0, Math.floor(Math.random() * bookmakers.length) + 1);

      selectedBookmakers.forEach(bookmaker => {
        coefficients.push({
          bookmaker,
          type,
          value: Math.random() * 3 + 1.5 // Random odds between 1.5 and 4.5
        });
      });
    });

    return coefficients;
  }

  /**
   * Generate mock authentication response
   * @returns {Object} Mock auth response
   */
  static generateMockAuthResponse() {
    return {
      accessToken: 'mock_access_token_' + Date.now(),
      refreshToken: 'mock_refresh_token_' + Date.now(),
      expiresIn: 1800, // 30 minutes
      user: {
        id: 1,
        email: 'user@example.com',
        role: 'User',
        firstName: 'Test',
        lastName: 'User'
      }
    };
  }

  /**
   * Generate mock subscription status
   * @returns {Object} Mock subscription status
   */
  static generateMockSubscriptionStatus() {
    const isActive = Math.random() > 0.3; // 70% chance of active
    const now = new Date();
    const expiryDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days from now

    return {
      isActive,
      expiryDate: isActive ? expiryDate.toISOString() : null,
      renewalDate: isActive ? expiryDate.toISOString() : null,
      plan: isActive ? 'Premium' : null
    };
  }
}

/**
 * Main Response Processor
 */
export class ResponseProcessor {
  constructor(options = {}) {
    this.cache = new ResponseCache();
    this.performanceMonitor = new ResponsePerformanceMonitor();
    this.enableCaching = options.enableCaching !== false;
    this.enableMocking = options.enableMocking || process.env.NODE_ENV === 'development';
    this.enableValidation = options.enableValidation !== false;
    this.enableTransformation = options.enableTransformation !== false;
  }

  /**
   * Process API response
   * @param {Object} response - Axios response object
   * @param {Object} options - Processing options
   * @returns {Object} Processed response
   */
  async processResponse(response, options = {}) {
    const requestId = options.requestId || `req_${Date.now()}`;
    const startTime = performance.now();

    try {
      // Extract response data
      const { data, status, headers, config } = response;
      const url = config.url;
      const method = config.method;

      // End performance timing
      this.performanceMonitor.endTiming(requestId, { url, method, status });

      // Create processed response object
      const processedResponse = {
        data: data,
        status: RESPONSE_STATUS.SUCCESS,
        metadata: {
          requestId,
          url,
          method,
          statusCode: status,
          headers: headers,
          processingTime: performance.now() - startTime,
          cached: false,
          validated: false,
          transformed: false
        },
        pagination: null,
        errors: [],
        warnings: []
      };

      // Validation
      if (this.enableValidation && options.schema) {
        const validation = ResponseValidator.validate(data, options.schema);
        processedResponse.metadata.validated = true;
        processedResponse.errors.push(...validation.errors);
        processedResponse.warnings.push(...validation.warnings);

        if (!validation.isValid) {
          this.performanceMonitor.recordValidationError();
          processedResponse.status = RESPONSE_STATUS.ERROR;
        }
      }

      // Specific validation for MarketsAnalysisDTO
      if (options.validateMarketsAnalysis && Array.isArray(data)) {
        const surebetValidation = ResponseValidator.validateMarketsAnalysis(data);
        processedResponse.errors.push(...surebetValidation.errors);
        processedResponse.warnings.push(...surebetValidation.warnings);

        if (!surebetValidation.isValid) {
          processedResponse.status = RESPONSE_STATUS.PARTIAL;
        }
      }

      // Transformation
      if (this.enableTransformation && options.transform) {
        try {
          processedResponse.data = await this.transformData(data, options.transform);
          processedResponse.metadata.transformed = true;
        } catch (transformError) {
          this.performanceMonitor.recordTransformationError();
          processedResponse.warnings.push(`Transformation error: ${transformError.message}`);
        }
      }

      // Pagination handling
      if (data && typeof data === 'object' && data.pagination) {
        processedResponse.pagination = PaginationHandler.processPaginatedResponse(data);
        processedResponse.data = processedResponse.pagination.items;
      }

      // Caching
      if (this.enableCaching && options.cache && method.toLowerCase() === 'get') {
        const cacheKey = this.cache.generateKey(url, config.params);
        const ttl = options.cache.ttl || CACHE_CONFIG.DEFAULT_TTL;
        this.cache.set(cacheKey, processedResponse, ttl);
      }

      return processedResponse;

    } catch (error) {
      this.performanceMonitor.endTiming(requestId, { error: error.message });

      return {
        data: null,
        status: RESPONSE_STATUS.ERROR,
        metadata: {
          requestId,
          processingTime: performance.now() - startTime,
          cached: false,
          validated: false,
          transformed: false
        },
        pagination: null,
        errors: [`Processing error: ${error.message}`],
        warnings: []
      };
    }
  }

  /**
   * Try to get cached response
   * @param {string} url - Request URL
   * @param {Object} params - Request parameters
   * @returns {Object|null} Cached response or null
   */
  getCachedResponse(url, params = {}) {
    if (!this.enableCaching) {
      return null;
    }

    const cacheKey = this.cache.generateKey(url, params);
    const cached = this.cache.get(cacheKey);

    if (cached) {
      this.performanceMonitor.recordCacheHit();
      cached.metadata.cached = true;
      cached.status = RESPONSE_STATUS.CACHED;
      return cached;
    }

    this.performanceMonitor.recordCacheMiss();
    return null;
  }

  /**
   * Transform response data
   * @param {*} data - Response data
   * @param {string|Function} transformer - Transformation type or function
   * @returns {*} Transformed data
   */
  async transformData(data, transformer) {
    if (typeof transformer === 'function') {
      return await transformer(data);
    }

    switch (transformer) {
      case 'marketsAnalysis':
        return ResponseTransformer.transformMarketsAnalysis(data);
      case 'userData':
        return ResponseTransformer.transformUserData(data);
      case 'pagination':
        return ResponseTransformer.transformPagination(data);
      default:
        return data;
    }
  }

  /**
   * Get performance metrics
   * @returns {Object} Performance metrics
   */
  getPerformanceMetrics() {
    return this.performanceMonitor.getMetrics();
  }

  /**
   * Get cache statistics
   * @returns {Object} Cache statistics
   */
  getCacheStats() {
    return this.cache.getStats();
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Reset performance metrics
   */
  resetMetrics() {
    this.performanceMonitor.reset();
  }
}

/**
 * Export individual classes
 */
export { ResponseCache, ResponsePerformanceMonitor, ResponseValidator, ResponseTransformer, PaginationHandler, MockResponseGenerator };

/**
 * Export response schemas
 */
export { RESPONSE_SCHEMAS };

/**
 * Create global response processor instance
 */
export const responseProcessor = new ResponseProcessor({
  enableCaching: true,
  enableMocking: process.env.NODE_ENV === 'development',
  enableValidation: true,
  enableTransformation: true
});

/**
 * Convenience functions for response processing
 */
export const processApiResponse = (response, options = {}) => {
  return responseProcessor.processResponse(response, options);
};

export const getCachedResponse = (url, params = {}) => {
  return responseProcessor.getCachedResponse(url, params);
};

export const validateResponse = (data, schema) => {
  return ResponseValidator.validate(data, schema);
};

export const validateMarketsAnalysis = (data) => {
  return ResponseValidator.validateMarketsAnalysis(data);
};

export const transformMarketsAnalysis = (data) => {
  return ResponseTransformer.transformMarketsAnalysis(data);
};

export const generateMockData = (type, count = 10) => {
  switch (type) {
    case 'marketsAnalysis':
      return MockResponseGenerator.generateMockMarketsAnalysis(count);
    case 'auth':
      return MockResponseGenerator.generateMockAuthResponse();
    case 'subscription':
      return MockResponseGenerator.generateMockSubscriptionStatus();
    default:
      return null;
  }
};

/**
 * Export response utilities
 */
export const responseUtils = {
  process: processApiResponse,
  getCached: getCachedResponse,
  validate: validateResponse,
  validateMarketsAnalysis,
  transform: transformMarketsAnalysis,
  generateMock: generateMockData,
  getMetrics: () => responseProcessor.getPerformanceMetrics(),
  getCacheStats: () => responseProcessor.getCacheStats(),
  clearCache: () => responseProcessor.clearCache(),
  resetMetrics: () => responseProcessor.resetMetrics()
};

export default {
  ResponseProcessor,
  ResponseCache,
  ResponsePerformanceMonitor,
  ResponseValidator,
  ResponseTransformer,
  PaginationHandler,
  MockResponseGenerator,
  responseProcessor,
  responseUtils,
  processApiResponse,
  getCachedResponse,
  validateResponse,
  validateMarketsAnalysis,
  transformMarketsAnalysis,
  generateMockData,
  RESPONSE_STATUS,
  RESPONSE_SCHEMAS
};
